<script>
  import { navigate } from "svelte-navigator";
  import { onMount } from "svelte";
  import {
    Card,
    TextInput,
    Select,
    RadioList,
    DatePicker,
    Button,
    Autocomplete,
    FileUpload,
  } from "../../components/index.svelte";
  import { translator, loading } from "../../store";

  import {
    getPropertyOwner,
    createPropertyOwner,
    updatePropertyOwner,
    getSystemUser,
    createSystemUser,
    updateSystemUser,
    getCities,
  } from "../../helpers/apiHelpers/index.svelte";

  import { toast, RequestError } from "../../helpers/utils.svelte";

  export let userID = null;
  export let formType = null;
  export let userType = null;

  let userMethods = {
    getter: getPropertyOwner,
    creator: createPropertyOwner,
    updater: updatePropertyOwner,
  };
  let cities = [];

  if (userType === "system-admins") {
    userMethods = {
      getter: getSystemUser,
      creator: createSystemUser,
      updater: updateSystemUser,
    };
  }

  let userDetails = null;
  let passwordRequirements = {
    min8Chars: {
      label: translator("En az 8 karakter"),
      valid: false
    },
    containUppercase: {
      label: translator("En az 1 büyük harf"),
      valid: false
    },
    containLowercase: {
      label: translator("En az 1 küçük harf"),
      valid: false
    },
    containDigit: {
      label: translator("En az 1 rakam"),
      valid: false
    },
    containSpecialChar: {
      label: translator("En az 1 özel karakter"),
      valid: false
    },
  };
  if (formType === "edit") {
    passwordRequirements = null;
  }

  function getFormTitle(formType, userType) {
    let formTitle = "";
    switch (true) {
      case formType === "new" && userType === "system-admins":
        formTitle = "Yeni Sistem Kullanıcısı";
        break;
      case formType === "new" && userType === "property-owners":
        formTitle = "Yeni Ev Sahibi";
        break;
      case formType === "edit" && userType === "system-admins":
        formTitle = "Sistem Kullanıcısını Düzenle";
        break;
      case formType === "edit" && userType === "property-owners":
        formTitle = "Ev Sahibini Düzenle";
        break;
    }
    return translator(formTitle);
  }

  function handlePasswordChange(e) {
    if (!e) {
      e = "";
    }
    passwordRequirements.containSpecialChar.valid = e.match(/\W|_/g)?.length > 0;
    passwordRequirements.containDigit.valid = e.match(/\d/g)?.length > 0;
    passwordRequirements.containLowercase.valid = e.match(/[a-z]/g)?.length > 0;
    passwordRequirements.containUppercase.valid = e.match(/[A-Z]/g)?.length > 0;
    passwordRequirements.min8Chars.valid = e.length >= 8;
  }

  function isPasswordValid(passwordRequirements) {
    let isValid = true;
    if (passwordRequirements) {
      Object.entries(passwordRequirements).forEach((r1) => {
        if (!r1[1].valid) {
          isValid = false;
        }
      });
    }
    return isValid;
  }

  onMount(async () => {
    try {
      $loading = true;
      cities = await getCities();
      if (formType === "edit" && userID) {
        let userResponse = await userMethods.getter(userID);
        userDetails = userResponse.user;
      }
      $loading = false;
    } catch (err) {
      toast.error(translator("Hata"), translator(err.getResponseMessage()));
    }
  });

  async function handleSubmit(e) {
    const userFormData = new FormData(e.target);
    userFormData.set('user_type', userType === "system-admins" ? "ADMIN" : "USER");
    try {
      let saveResp = null;
      if (formType === "edit" && userID) {
        saveResp = await userMethods.updater(userID, userFormData);
      } else {
        saveResp = await userMethods.creator(userFormData);
      }
      if (!saveResp.id) {
        throw new RequestError("Bilinmeyen Hata #o4Wi", {
          message: "Bilinmeyen Hata #o4Wi",
        });
      }
      window.scrollTo({ top: 0 });
      navigate(-1);
      toast.success(
        translator("Başarılı"),
        formType === "edit"
          ? translator("Değişiklikler Kaydedildi")
          : translator("Kullanıcı oluşturuldu")
      );
    } catch (err) {
      toast.error(translator("Hata"), translator(err.getResponseMessage()));
    }
  }
</script>

<Card headerText={getFormTitle(formType, userType)}>
  <form
    class="basic-form"
    id="user-form"
    on:submit|preventDefault={handleSubmit}
  >
    <div class="row">
      <div class="col-md-12 p-2">
        <FileUpload
          fieldName="profile_picture"
          multiple={false}
          label={translator("Profil fotoğrafı")}
          selectedFiles={userDetails?.pp_file ? [userDetails.pp_file] : []}
        />
      </div>
      <div class="col-md-6 p-2">
        <TextInput
          fieldName="firstname"
          label={translator("Ad")}
          required
          value={userDetails?.firstname}
        />
      </div>
      <div class="col-md-6 p-2">
        <TextInput
          fieldName="lastname"
          label={translator("Soyad")}
          required
          value={userDetails?.lastname}
        />
      </div>
      <div class="col-md-6 p-2">
        <TextInput
          fieldName="email"
          label={translator("E-mail")}
          required
          value={userDetails?.email}
          type="email"
        />
      </div>
      <div class="col-md-6 p-2">
        <TextInput
          fieldName="phone_number"
          label={translator("Telefon Numarası")}
          value={userDetails?.phone_number}
          type="phone"
        />
      </div>
      {#if formType === "new"}
        <div class="col-md-12 p-2">
          <TextInput
            fieldName="password"
            label={translator("Şifre")}
            required
            type="password"
            onChange={handlePasswordChange}
          />
          <div class="p-4">
            <ul class="list-group">
              {#each Object.keys(passwordRequirements) as requirement}
                <li class="list-group-item d-flex justify-content-between align-items-center"
                    class:text-red={!passwordRequirements[requirement].valid}
                    class:text-green={passwordRequirements[requirement].valid}
                    >
                  {passwordRequirements[requirement].label}
                </li>
              {/each}
          </ul>
          </div>
        </div>
      {/if}
      <div class="col-md-6 p-2">
        <DatePicker
          fieldName="birth_date"
          value={userDetails?.birth_date}
          label={translator("Doğum Tarihi")}
          required
        />
      </div>
      <div class="col-md-6 p-2">
        <Select
          fieldName="gender"
          required
          label={translator("Cinsiyet")}
          value={userDetails?.gender}
          selectOptions={[
            { text: "Erkek", value: "M" },
            { text: "Kadın", value: "F" },
          ]}
        />
      </div>
      {#if userType !== "system-admins"}
        <div class="col-md-6 p-2">
          <Autocomplete
            required
            fieldName={"city_id"}
            label={translator("Şehir")}
            labelField="name"
            valueField="id"
            placeholder={translator("Lütfen Şehir Seçiniz")}
            options={cities}
            value={userDetails?.city_id}
          />
        </div>
      {/if}
      <div class:col-md-12={userType === "system-admins"} class:col-md-6={userType !== "system-admins"} class="p-2">
        <Select
          fieldName="lang"
          label={translator("Dil")}
          value={userDetails?.lang}
          required
          selectOptions={[
            { text: "Türkçe", value: "tr" },
            { text: "İngilizce", value: "en" },
          ]}
        />
      </div>
      <div class="col-lg-4 col-md-12 p-2">
        <RadioList
          label={translator("Kullanıcı Durumu")}
          groupName="is_active"
          value={userDetails?.is_active || 1}
          options={[
            { label: translator("Aktif"), value: "1" },
            { label: translator("Pasif"), value: "0" },
          ]}
        />
      </div>
      <div class="col-lg-4 col-md-12 p-2">
        <RadioList
          label={translator("Bildirim Aboneliği")}
          groupName="has_notification_subscription"
          value={userDetails?.has_notification_subscription || 1}
          options={[
            { label: translator("Etkin"), value: "1" },
            { label: translator("Devre Dışı"), value: "0" },
          ]}
        />
      </div>
      <div class="col-lg-4 col-md-12 p-2">
        <RadioList
          label={translator("Email Aboneliği")}
          groupName="has_email_subscription"
          value={userDetails?.has_email_subscription || 1}
          options={[
            { label: translator("Etkin"), value: "1" },
            { label: translator("Devre Dışı"), value: "0" },
          ]}
        />
      </div>
    </div>
    <hr />
    <div class="gap-2 d-flex justify-content-end align-items-center">
      <Button formName="user-form" colorCls="btn-success" disabled={!isPasswordValid(passwordRequirements)}>
        {translator("Kaydet")}
      </Button>
      <Button onClick={() => navigate(-1)} colorCls="btn-dark">
        {translator("Vazgeç")}
      </Button>
    </div>
  </form>
</Card>
